import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Code,
  FileText,
  Layers,
  Plug,
  Rocket,
  Settings,
  Sparkles,
  Wrench,
} from "lucide-react";

export type DocsNavItem = {
  id: string;
  label: string;
  icon: LucideIcon;
};

export type DocsNavGroup = {
  title: string;
  items: DocsNavItem[];
};

export type DocsTocItem = {
  id: string;
  label: string;
};

export type DocsSectionBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "steps"; items: { title: string; body: string }[] }
  | { type: "callout"; title: string; text: string }
  | { type: "links"; items: { label: string; href: string; description?: string }[] };

export type DocsPageSection = {
  id: string;
  title: string;
  description: string;
  toc: DocsTocItem[];
  blocks: DocsSectionBlock[];
};

export const DOCS_NAV_GROUPS: DocsNavGroup[] = [
  {
    title: "Quickstarts",
    items: [
      { id: "welcome", label: "Welcome to Inbound GPT", icon: BookOpen },
      { id: "getting-started", label: "Getting started", icon: Rocket },
      { id: "first-agent", label: "Your first agent", icon: Sparkles },
      { id: "go-live", label: "Go live", icon: Layers },
    ],
  },
  {
    title: "Configure",
    items: [
      { id: "profile", label: "Agent profile", icon: Settings },
      { id: "responsibilities", label: "Responsibilities", icon: Wrench },
      { id: "training", label: "Training & knowledge", icon: FileText },
      { id: "tools", label: "Tools & appointments", icon: Plug },
    ],
  },
  {
    title: "Connect",
    items: [
      { id: "integrations", label: "Integrations", icon: Plug },
      { id: "widget", label: "Widget installation", icon: Code },
      { id: "channels", label: "Phone, SMS & email", icon: Layers },
    ],
  },
  {
    title: "Operate",
    items: [
      { id: "handoffs", label: "Team handoffs", icon: Sparkles },
      { id: "best-practices", label: "Best practices", icon: BookOpen },
      { id: "help", label: "Get help", icon: FileText },
    ],
  },
];

export const DOCS_SECTIONS: Record<string, DocsPageSection> = {
  welcome: {
    id: "welcome",
    title: "Welcome to Inbound GPT",
    description:
      "Inbound GPT helps businesses never miss a call, visitor, or booking — with human-like AI agents and a human always in the loop.",
    toc: [
      { id: "what-you-can-build", label: "What you can build" },
      { id: "how-it-fits", label: "How it fits together" },
      { id: "next-step", label: "Your next step" },
    ],
    blocks: [
      {
        type: "list",
        items: [
          "Answer inbound calls and chats 24/7",
          "Book and reschedule appointments in your calendar or EMR",
          "Capture and qualify leads with full conversation context",
          "Hand off to your team with whisper messages and inbox summaries",
        ],
      },
      {
        type: "paragraph",
        text: "Configure a named agent with a profile, responsibilities (channels), tools (calendars, EMR/MCP), and training (knowledge base), then deploy across phone, chat, SMS, email, and more. Clone Official or Moonlight templates, or request a custom build. Inbound GPT is not a clinical tool — it does not provide medical advice or diagnoses.",
      },
      {
        type: "links",
        items: [
          {
            label: "Browse agent templates",
            href: "/agents",
            description: "Clone battle-tested agents by industry and role",
          },
          {
            label: "Build your agent",
            href: "/agents/hire/custom",
            description: "Self-serve hire onboarding with unit-based pricing",
          },
          {
            label: "AI Summary",
            href: "/ai-summary",
            description: "Machine-readable platform overview",
          },
        ],
      },
    ],
  },
  "getting-started": {
    id: "getting-started",
    title: "Getting started",
    description: "Create your account, pick a template or custom hire path, and reach a working preview in minutes.",
    toc: [
      { id: "create-account", label: "Create an account" },
      { id: "choose-path", label: "Choose a path" },
      { id: "preview", label: "Preview before you go live" },
    ],
    blocks: [
      {
        type: "steps",
        items: [
          {
            title: "Sign up",
            body: "Create an Inbound GPT account from Sign up. No credit card is required to start with free Live Chat and included usage tiers.",
          },
          {
            title: "Choose a template or build custom",
            body: "Browse Agent Templates to clone a proven role, or use Build your agent to describe responsibilities, tools, and training from scratch. Add your company profile (name, website, locations, team) so greetings and handoffs are correct.",
          },
          {
            title: "Test in the widget",
            body: "Use Test / Preview to run chat and phone scenarios before publishing. Tip: start with Live Chat only, then add Phone, SMS, or EMR once knowledge answers are solid.",
          },
        ],
      },
    ],
  },
  "first-agent": {
    id: "first-agent",
    title: "Your first agent",
    description: "Walk through profile, responsibilities, training, and tools — the same structure as Settings in the dashboard.",
    toc: [
      { id: "profile", label: "Profile" },
      { id: "channels", label: "Channels" },
      { id: "knowledge", label: "Knowledge" },
      { id: "tools", label: "Tools" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Profile — persona, voice, team members, and locations so every conversation feels on-brand.",
      },
      {
        type: "paragraph",
        text: "Responsibilities — enable live chat, phone, SMS, email, video, and more. Start with Live Chat, then add paid channels.",
      },
      {
        type: "paragraph",
        text: "Training — company profile, FAQs, files, and website RAG so answers stay accurate.",
      },
      {
        type: "links",
        items: [
          { label: "Agent Profile", href: "/product/profile" },
          { label: "Responsibilities", href: "/product/responsibilities" },
          { label: "Training", href: "/product/training" },
          { label: "Tools", href: "/product/tools" },
          { label: "Integrations", href: "/product/integrations" },
        ],
      },
    ],
  },
  "go-live": {
    id: "go-live",
    title: "Go live",
    description: "Publish the chat widget, connect a phone number, and confirm handoffs before you announce to customers.",
    toc: [
      { id: "widget", label: "Install the widget" },
      { id: "phone", label: "Connect phone" },
      { id: "handoff", label: "Verify handoffs" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Copy the embed snippet from Channels → Live Chat and place it on your site. Confirm the agent greeting and CTAs look correct on mobile and desktop.",
      },
      {
        type: "paragraph",
        text: "Enable Phone Calls if needed, assign a number, and run a test call. Confirm caller ID handling and business hours behavior.",
      },
      {
        type: "callout",
        title: "Before you announce",
        text: "Verify team handoff (whisper + Lead Team Member phone), knowledge answers top FAQs, and appointment booking works in a sandbox or test calendar.",
      },
    ],
  },
  profile: {
    id: "profile",
    title: "Agent profile",
    description: "Set persona, voice, team members, and locations so every conversation feels on-brand.",
    toc: [
      { id: "persona", label: "Persona & voice" },
      { id: "team", label: "Team members" },
      { id: "locations", label: "Locations" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Configure name, bio, personality tone, language skills, and voice inflection so the agent introduces itself consistently on every channel.",
      },
      {
        type: "paragraph",
        text: "Add team members with titles and transfer numbers so the agent can hand off to the right person with whisper context.",
      },
      {
        type: "links",
        items: [
          {
            label: "Product — Profile",
            href: "/product/profile",
            description: "Locations, hours, lead contacts, and dedicated agent email/phone",
          },
        ],
      },
    ],
  },
  responsibilities: {
    id: "responsibilities",
    title: "Responsibilities (channels)",
    description: "Enable the channels your agent should handle — matching My Responsibilities in Settings.",
    toc: [
      { id: "chat", label: "Live chat" },
      { id: "voice", label: "Phone & video" },
      { id: "async", label: "SMS & email" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Live Chat powers the website widget. It is free with unlimited conversations — the best place to start before enabling paid channels.",
      },
      {
        type: "paragraph",
        text: "Phone Calls and Video include monthly fees plus free minutes, then per-minute usage. Browser Calls are free and unlimited.",
      },
      {
        type: "links",
        items: [
          {
            label: "Product — Responsibilities",
            href: "/product/responsibilities",
            description: "SMS, email, WhatsApp, Messenger, and full channel list",
          },
          { label: "Pricing calculator", href: "/pricing" },
        ],
      },
    ],
  },
  training: {
    id: "training",
    title: "Training & knowledge",
    description: "Teach the agent your services, policies, and FAQs with company profile and RAG sources.",
    toc: [
      { id: "basic", label: "Basic training" },
      { id: "advanced", label: "Advanced sources" },
      { id: "test", label: "Test answers" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Basic training (company profile and ICP) is included free. Fill services, policies, brand voice, and ideal customer profile so answers stay on-message.",
      },
      {
        type: "paragraph",
        text: "Advanced training adds website crawl, files, Q&A pairs, video, and audio — billed as a monthly add-on when enabled. Prefer short, factual Q&A for scheduling and pricing questions.",
      },
      {
        type: "links",
        items: [
          {
            label: "Product — Training",
            href: "/product/training",
            description: "Run knowledge tests before you enable phone or go live",
          },
        ],
      },
    ],
  },
  tools: {
    id: "tools",
    title: "Tools & appointments",
    description: "Connect calendars, EMR/PMS, and MCP servers so the agent can book and look up real availability.",
    toc: [
      { id: "calendars", label: "Calendars" },
      { id: "emr", label: "EMR / PMS" },
      { id: "mcp", label: "MCP servers" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Google Calendar, Calendly, and Zoom are scheduling add-ons. Always test booking in a non-production calendar first.",
      },
      {
        type: "paragraph",
        text: "Juvonno, NexHealth, and dental PMS integrations let the agent check real availability and create appointments in your system of record.",
      },
      {
        type: "links",
        items: [
          { label: "Product — Tools", href: "/product/tools" },
          { label: "Integrations overview", href: "/product/integrations" },
        ],
      },
    ],
  },
  integrations: {
    id: "integrations",
    title: "Integrations",
    description: "Connect analytics, automation, dedicated phone/email, and custom workflows.",
    toc: [
      { id: "native", label: "Native integrations" },
      { id: "automation", label: "Automation" },
      { id: "custom", label: "Custom development" },
    ],
    blocks: [
      {
        type: "list",
        items: [
          "Dedicated phone number and agent email",
          "EMR/PMS and calendar tools under Agent Tools",
          "WhatConverts & Google Analytics — attribution and traffic",
        ],
      },
      {
        type: "paragraph",
        text: "Zapier connects Inbound GPT events to thousands of apps. Use it for CRM updates, Slack alerts, and custom lead routing.",
      },
      {
        type: "links",
        items: [
          { label: "Integrations", href: "/product/integrations" },
          {
            label: "Custom Agent Development",
            href: "/custom-agent-development",
            description: "Request a new MCP workflow or role that is not available yet",
          },
          { label: "API Reference", href: "/api-reference" },
        ],
      },
    ],
  },
  widget: {
    id: "widget",
    title: "Widget installation",
    description: "Add the Inbound GPT chat widget to your website with a single embed snippet.",
    toc: [
      { id: "install", label: "Install" },
      { id: "customize", label: "Customize" },
      { id: "verify", label: "Verify" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "In project Settings → Channels → Live Chat, copy the embed code and paste it before </body> on every page where visitors should see the agent (or load it site-wide via your tag manager).",
      },
      {
        type: "paragraph",
        text: "Check colors, greeting, and CTAs (Book a Demo, Connect with Human) match your brand on mobile and desktop.",
      },
      {
        type: "callout",
        title: "Deleted projects",
        text: "If you delete a project, remove the widget from your site. Orphaned embeds can cause errors for visitors.",
      },
    ],
  },
  channels: {
    id: "channels",
    title: "Phone, SMS & email",
    description: "Enable voice and async channels after chat is working reliably.",
    toc: [
      { id: "phone", label: "Phone" },
      { id: "sms", label: "SMS" },
      { id: "email", label: "Email" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "Phone Calls include a monthly fee plus free minutes, then per-minute usage. Configure business hours and transfer contacts, and confirm E.164 formats for test calls, before you go live.",
      },
      {
        type: "paragraph",
        text: "SMS includes a monthly fee, free messages, then per-SMS usage — useful for reminders and follow-ups after chat or phone.",
      },
      {
        type: "links",
        items: [
          {
            label: "Pricing",
            href: "/pricing",
            description: "Email is a flat monthly add-on with unlimited messages",
          },
        ],
      },
    ],
  },
  handoffs: {
    id: "handoffs",
    title: "Team handoffs",
    description: "Keep a human in the loop with warm transfers, whisper context, and inbox summaries.",
    toc: [
      { id: "transfer", label: "Live transfer" },
      { id: "inbox", label: "Inbox & summaries" },
    ],
    blocks: [
      {
        type: "paragraph",
        text: "When a caller asks for a person — or the agent cannot resolve the request — enhanced transfer connects them to a Lead Team Member with a short whisper message summarizing why they called. Set Lead Team Member phone numbers per location first.",
      },
      {
        type: "list",
        items: [
          "Use Connect with Human CTAs in the chat widget",
          "Review Conversation Summary emails after each handoff",
          "Confirm whisper context is clear for staff answering transfers",
        ],
      },
    ],
  },
  "best-practices": {
    id: "best-practices",
    title: "Best practices",
    description: "Tips to keep answers accurate, bookings reliable, and handoffs smooth.",
    toc: [
      { id: "knowledge", label: "Knowledge" },
      { id: "booking", label: "Booking" },
      { id: "ops", label: "Operations" },
    ],
    blocks: [
      {
        type: "list",
        items: [
          "Write FAQs in the voice you want the agent to use — short sentences, clear next steps",
          "Review inbox weekly for knowledge gaps and missed intents",
        ],
      },
      {
        type: "paragraph",
        text: "Test booking end-to-end in chat before enabling phone booking. Prefer a sandbox calendar or EMR until you trust the flow.",
      },
      {
        type: "links",
        items: [
          { label: "Customer Stories", href: "/customer-stories" },
          { label: "Help Center", href: "/help-center" },
          { label: "FAQ", href: "/faq", description: "Keep hours updated; start with one location and role" },
        ],
      },
    ],
  },
  help: {
    id: "help",
    title: "Get help",
    description: "Where to go for product help, API details, and custom builds.",
    toc: [
      { id: "resources", label: "Resources" },
      { id: "custom", label: "Custom work" },
    ],
    blocks: [
      {
        type: "links",
        items: [
          { label: "Help Center", href: "/help-center", description: "Setup, pricing, HITL, and channel guides" },
          { label: "API Reference", href: "/api-reference", description: "Webhooks and developer resources" },
          { label: "Contact sales", href: "/about", description: "Talk to the team" },
        ],
      },
      {
        type: "links",
        items: [
          {
            label: "Custom Agent Development",
            href: "/custom-agent-development",
            description: "Request a custom build or new role template",
          },
        ],
      },
    ],
  },
};

export const DEFAULT_DOCS_SECTION_ID = "welcome";

export function getDocsSection(id: string): DocsPageSection {
  return DOCS_SECTIONS[id] ?? DOCS_SECTIONS[DEFAULT_DOCS_SECTION_ID]!;
}
